import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { Translate } from '~/shared/internationalization/translation';

import type { GrowthGoal, GrowthMeasure } from '../../data-contract/growth';
import { DetailSection } from '../components/GrowthTileFrame';
import { measureProgress, useFormatDay } from '../components/goal-display';

const Figure: FunctionComponent<{ readonly main: ReactNode; readonly rest?: ReactNode }> = ({ main, rest }) => (
    <span className="flex items-baseline gap-s">
        <span className="text-h4 tabular-nums text-text-heading">{main}</span>
        {rest === undefined ? null : <span className="text-body-s tabular-nums text-text-secondary">{rest}</span>}
    </span>
);

const figureFor = (
    measure: GrowthMeasure,
    translate: Translate,
): { readonly caption?: string; readonly figure: ReactNode } => {
    switch (measure.type) {
        case 'number':
            return {
                caption: translate('pages.growth.detail.started-at', { value: `${measure.start} ${measure.unit}` }),
                figure: <Figure main={measure.current} rest={`/ ${measure.target} ${measure.unit}`} />,
            };
        case 'percent':
            return {
                figure: <Figure main={translate('pages.growth.measure-values.percent', { value: measure.current })} />,
            };
        case 'milestones':
            return {
                figure: (
                    <Figure
                        main={measure.milestones.filter(({ isDone }) => isDone).length}
                        rest={translate('pages.growth.measure-values.milestones-total', {
                            count: measure.milestones.length,
                        })}
                    />
                ),
            };
        case 'done':
            return {
                figure: (
                    <Figure
                        main={translate(
                            measure.isDone ? 'pages.growth.states.done' : 'pages.growth.detail.not-done-yet',
                        )}
                    />
                ),
            };
    }
};

export const ProgressSection: FunctionComponent<{ readonly goal: GrowthGoal }> = ({ goal }) => {
    const translate = useTranslate();
    const formatDay = useFormatDay();
    const { measure } = goal;
    const due = is.nonEmptyString(goal.due)
        ? translate('pages.growth.checkpoint.due', { date: formatDay(goal.due) })
        : translate('pages.growth.detail.no-due');

    if (is.nullOrUndefined(measure)) {
        return (
            <DetailSection title={translate('pages.growth.detail.progress')}>
                <div className="flex items-baseline justify-between gap-m rounded-xl border border-border-subtle p-xl">
                    <span className="text-body-s text-text-muted">{translate('pages.growth.detail.no-measure')}</span>
                    <span className="text-body-s tabular-nums text-text-secondary">{due}</span>
                </div>
            </DetailSection>
        );
    }

    const progress = measureProgress(measure);
    const { caption, figure } = figureFor(measure, translate);

    return (
        <DetailSection title={translate('pages.growth.detail.progress')}>
            <div className="flex w-full flex-col gap-m rounded-xl border border-border-subtle p-xl">
                <div className="flex items-baseline justify-between gap-m">
                    {figure}
                    <span className="text-body-s tabular-nums text-text-secondary">{due}</span>
                </div>
                {progress === undefined ? null : (
                    <div className="h-xs w-full overflow-hidden rounded-full bg-surface-hover">
                        <div
                            className="h-full rounded-full bg-surface-inverse transition-[width] duration-300 ease-out"
                            style={{ width: `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%` }}
                        />
                    </div>
                )}
                {caption === undefined ? null : <span className="text-body-xs text-text-muted">{caption}</span>}
            </div>
        </DetailSection>
    );
};
